import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Button, Stack, Typography, alpha } from "@mui/material"
import type { Theme } from "@mui/material"
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined"
import type { GridColDef } from "@mui/x-data-grid"

import type { UsersLog } from "types/users.types"

import { useAllUsersLogs } from "helpers/hooks/useAllUsersLogs"
import useSelectOptions from "helpers/hooks/useSelectOptions"
import { buildGridCsv, saveCsv } from "helpers/utils/csv"
import { formatDateFromISO } from "helpers/utils/dateUtils"
import PopUp from "widgets/common/PopUp"
import Spinner from "widgets/common/Spinner"

const neutralOutlinedSx = { color: "text.secondary", borderColor: "neutral.400" }

const neutralTextSx = {
  color: "grey.500",
  "&:hover": { backgroundColor: (theme: Theme) => alpha(theme.palette.grey[500], 0.1) },
}

interface TitleTextStackProps {
  title?: string
  body: string
}

const TitleTextStack = ({ title, body }: TitleTextStackProps) => (
  <Stack spacing={0.75}>
    {title && <Typography variant="h4">{title}</Typography>}
    <Typography color="neutral.600">{body}</Typography>
  </Stack>
)

interface AuditTrailCsvModalProps {
  open: boolean
  onClose: () => void
  columns: GridColDef<UsersLog>[]
  range?: string | null
  fromDate?: string | null
  toDate?: string | null
  fileName: string
}

// The table only holds the pages already seen, so the CSV fetches the whole range itself.
const AuditTrailCsvModal = ({
  open,
  onClose,
  columns,
  range,
  fromDate,
  toDate,
  fileName,
}: AuditTrailCsvModalProps) => {
  const { t } = useTranslation()
  const { dateRefOptions } = useSelectOptions()
  // Closing disables it, which drops whatever is still pending.
  const { logs, fetchedCount, status, retry, reset } = useAllUsersLogs({
    fromDate,
    toDate,
    enabled: open,
  })
  const hasNoLogs = status === "done" && logs.length === 0

  // The picker's own label, or the dates for a custom period.
  const collectingText = useMemo(() => {
    const label =
      range !== "custom" && dateRefOptions.find(({ value }) => value === range)?.label
    if (label) {
      return t("audit_trail.CSV_COLLECTING", { period: t(label) })
    }
    return t("audit_trail.CSV_COLLECTING_CUSTOM", {
      from: formatDateFromISO(fromDate),
      to: formatDateFromISO(toDate),
    })
  }, [range, fromDate, toDate, dateRefOptions, t])

  useEffect(() => {
    if (status !== "done" || logs.length === 0) {
      return
    }
    saveCsv(buildGridCsv(logs, columns), fileName)
    onClose()
  }, [status, logs, columns, fileName, onClose])

  return (
    // Reset only once it is gone, or it fades out showing the loading state.
    <PopUp open={open} onClose={onClose} TransitionProps={{ onExited: reset }}>
      <Stack
        spacing={2}
        alignItems="center"
        textAlign="center"
        sx={{ width: 360, maxWidth: "100%" }}
      >
        {status === "error" ? (
          <>
            <ErrorOutlinedIcon color="error" sx={{ fontSize: 36 }} />
            <TitleTextStack
              title={t("audit_trail.CSV_EXPORT_FAILED")}
              body={t("audit_trail.CSV_COLLECT_FAILED")}
            />
            <Stack spacing={1} width="100%">
              <Button variant="contained" size="small" fullWidth onClick={retry}>
                {t("generic.RETRY")}
              </Button>
              <Button size="small" fullWidth onClick={onClose} sx={neutralTextSx}>
                {t("generic.CLOSE")}
              </Button>
            </Stack>
          </>
        ) : hasNoLogs ? (
          <>
            <TitleTextStack body={t("audit_trail.CSV_NO_LOGS")} />
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={onClose}
              sx={neutralOutlinedSx}
            >
              {t("generic.CLOSE")}
            </Button>
          </>
        ) : (
          <>
            <Spinner size="36px" disableGutters />
            <TitleTextStack
              title={t("audit_trail.CSV_PREPARING")}
              body={collectingText}
            />
            <Typography
              color="neutral.500"
              sx={{ lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
            >
              {fetchedCount
                ? t("audit_trail.CSV_COLLECTED", { count: fetchedCount })
                : t("audit_trail.CSV_STARTING")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={onClose}
              sx={neutralOutlinedSx}
            >
              {t("audit_trail.CSV_CANCEL")}
            </Button>
          </>
        )}
      </Stack>
    </PopUp>
  )
}

export default AuditTrailCsvModal
